<template>
  <v-card style="overflow: hidden;">
    <v-card-title>
      <span v-if="bankInfo">Edit Banking Information</span>
      <span v-else>Add Banking Information</span>
    </v-card-title>
    <v-form ref="form" v-model="valid">
      <v-card-text>
        <v-container class="pl-0 pr-0">
          <v-row v-if="!bankInfo">
            <v-col cols="12">
              <v-text-field
                v-model="companyName"
                label="Account Holder Name"
                hint="We cannot pay a third party. This must be identical to your account name on file with us."
                :persistent-hint="true"
                :rules="[rules.required]"
                dense
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="bankInstitution"
                label="Bank/Institution Name"
                :rules="[rules.required]"
                dense
              ></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-select
                v-model="accountType"
                label="Account Type"
                item-text="title"
                item-value="id"
                :items="[
                    { id: '1', title: 'Checking' },
                    { id: '2', title: 'Savings' },
                  ]"
                :rules="[rules.required]"
                dense
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <v-text-field
                v-model="accountNumber"
                :append-icon="showAccountNumber ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showAccountNumber ? 'number' : 'password'"
                @click:append="showAccountNumber = !showAccountNumber"
                :label="
                    bankInfo ? bankInfo.maskedAccountNumber : 'Account Number'
                  "
                :id="dynamicID()"
                :hint="
                    bankInfo
                      ? 'Leave blank to keep account number unchanged'
                      : null
                  "
                :persistent-hint="true"
                :rules="[
                    ...characterLimit(20),
                    bankInfo ? true : rules.required,
                  ]"
                dense
              />
            </v-col>
            <v-col cols="6">
              <v-text-field
                type="number"
                v-model="routingNumber"
                @blur="deriveBranchNumber"
                label="Routing Number"
                :rules="[...characterLimit(9), rules.required]"
                dense
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6" class="pt-0">
              <span>Transaction Types</span>
              <span class="text-caption"> (Choose at least one)</span>
              <v-row>
                <v-col cols="10" class="pt-4 pb-0">
                  <v-checkbox
                    v-model="tranTypes"
                    label="ACH Debit"
                    :value="BankTranTypes.DEBIT"
                    :disabled="isCurrentPrimary(BankTranTypes.DEBIT)"
                    hint="Payments from you to us"
                    :persistent-hint="true"
                    :rules=[rules.transTypes]
                  ></v-checkbox>
                </v-col>
                <v-col cols="2" class="pt-4 pb-0">
                  <v-tooltip
                    bottom
                    v-if="isCurrentPrimary(BankTranTypes.DEBIT)"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="mt-1"
                        color="gray"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span
                    >You may not disable this payment method until you create
                        a new primary for this transaction type.</span
                    >
                  </v-tooltip>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="10" class="pt-0 pb-0">
                  <v-checkbox
                    v-model="tranTypes"
                    label="ACH Payment"
                    :value="BankTranTypes.DEPOSIT"
                    :disabled="isCurrentPrimary(BankTranTypes.DEPOSIT)"
                    hint="Payments from us to you"
                    :persistent-hint="true"
                    :rules=[rules.transTypes]
                  ></v-checkbox>
                </v-col>
                <v-col cols="2" class="pt-0 pb-0">
                  <v-tooltip
                    bottom
                    v-if="isCurrentPrimary(BankTranTypes.DEPOSIT)"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        class="mt-1"
                        color="gray"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-help-circle-outline
                      </v-icon>
                    </template>
                    <span
                    >You may not disable this payment method until you create
                        a new primary for this transaction type.</span
                    >
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="6">
              <v-text-field
                v-model="companyPin"
                :rules="[rules.required]"
                :append-icon="showPin ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPin ? 'text' : 'password'"
                @click:append="showPin = !showPin"
                label="Company Pin"
                dense
                :id="dynamicID()"
                hint="Please call the Trading Desk if you forgot your Company Pin."
                :persistent-hint="true"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="pb-0">
              <span class="text-h6">ACH Authorization</span>
              <div colored-border style="height: 10rem; overflow-y: scroll !important; border: 6px solid #eaebed;">
                <authorization-content></authorization-content>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-row class="mx-3">
        <v-col class="pt-1">
          <v-checkbox
            v-model="acceptACHTerms"
            required
            :rules="[rules.required]"
            label="I agree to the ACH Authorization"
            class="mt-2"
          ></v-checkbox>
        </v-col>
        <v-col class="pt-1">
          <v-card-actions>
            <v-spacer/>
            <v-btn color="white" @click="close" class="mr-2"> Cancel</v-btn>
            <v-btn
              color="primary"
              class="lighten-1"
              @click="save"
              :loading="loading"
            >
              Submit
            </v-btn>
          </v-card-actions>
        </v-col>
      </v-row>
    </v-form>
  </v-card>
</template>

<script>
import orderMixin from '@/mixins/order'
import companyMixin from '@/mixins/company'
import Util from '@/util'
import {BankTranTypes} from '@/util/constants'
import {mapGetters} from 'vuex'
import AuthorizationContent from '@/components/AchAuthorizationContent'

export default {
  props: ['bankInfo', 'defaultTranType', 'shown'],
  components: {AuthorizationContent},
  computed: {
    ...mapGetters([
      'getSelectedCompany',
      'getPrimaryDebitBankAccount',
      'getPrimaryDepositBankAccount',
    ]),
  },
  watch: {
    bankInfo: {
      handler() {
        this.resetForm()
      },
    },
    shown: {
      handler() {
        this.resetForm()
      },
    },
  },
  async mounted() {
    await Promise.all([this.$store.dispatch(`fetchCountries`)])
    this.resetForm()
  },
  mixins: [companyMixin, orderMixin],
  data() {
    return {
      showPin: false,
      valid: null,
      companyName: null,
      bankInstitution: null,
      maskedAccountNumber: null,
      acceptACHTerms: null,
      accountHash: null,
      branchNumber: null,
      routingNumber: null,
      accountNumber: null,
      showAccountNumber: false,
      tranTypes: [],
      agree: null,
      accountType: null,
      companyPin: null,
      loading: false,
      primary: {
        [BankTranTypes.DEBIT]: false,
        [BankTranTypes.DEPOSIT]: false,
      },
      rules: {
        required: (value) => !!value || 'This field is required',
        transTypes: () => this.tranTypes.length > 0 || 'Must select a Transaction Type',
      },
      BankTranTypes,
    }
  },
  methods: {
    dynamicID: Util.dynamicID,
    isCurrentPrimary(tranType) {
      const getter =
        tranType === BankTranTypes.DEPOSIT
          ? this.getPrimaryDepositBankAccount
          : this.getPrimaryDebitBankAccount
      return (
        this.bankInfo &&
        this.bankInfo.accountHash &&
        getter &&
        getter.accountHash === this.bankInfo.accountHash
      )
    },
    deriveBranchNumber() {
      if (this.routingNumber && !this.branchNumber)
        this.branchNumber = this.routingNumber.replace(/\D/g, '').slice(0, 5)
    },
    resetForm() {
      this.$refs.form.resetValidation()
      if (this.bankInfo) {
        this.accountHash = this.bankInfo.accountHash
        this.bankInstitution = this.bankInfo.bankInstitution
        this.routingNumber = this.bankInfo.routingNumber
        this.accountNumber = null
        this.accountType = this.bankInfo.accountType
        this.companyName = this.bankInfo.companyName
        this.tranTypes = this.bankInfo.tranTypes
      } else {
        this.accountHash = this.bankInstitution = this.branchNumber = this.routingNumber = this.accountNumber = null
        this.tranTypes = this.defaultTranType ? [this.defaultTranType] : []
        this.accountType = `1`
        this.companyName = this.getSelectedCompany.name
      }
      this.companyPin = null
      this.showAccountNumber = false
    },
    close() {
      this.resetForm()
      this.$emit('close')
    },
    async save() {
      this.deriveBranchNumber()
      if (this.$refs.form.validate()) {
        const {
          companyName,
          bankInstitution,
          branchNumber,
          routingNumber,
          accountNumber,
          accountType,
          companyPin,
          tranTypes,
          accountHash,
        } = this
        try {
          this.loading = true
          await this.$store.dispatch(`submitBankingInformation`, {
            companyName,
            bankInstitution,
            branchNumber,
            routingNumber,
            accountNumber: accountNumber || undefined,
            accountHash,
            accountType,
            companyPin,
            tranTypes,
            primary: {
              debit: tranTypes.indexOf(BankTranTypes.DEBIT) > -1,
              deposit: tranTypes.indexOf(BankTranTypes.DEPOSIT) > -1,
            },
            active: true,
          })
          this.$emit('save')
          this.resetForm()
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: `Your banking information has been successfully submitted.`,
            color: 'green',
          })
        } catch (e) {
          window._VMA.$emit('SHOW_SNACKBAR', {
            text: e.response && e.response.data ? e.response.data : e.message,
            color: 'red',
          })
        } finally {
          this.loading = false
        }
      }
    },
  },
}
</script>
